import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { RestService } from 'src/app/services/rest.service';
import { OverviewLicenceDto } from 'src/app/modules/overview/models/overview-licence-dto';

export interface DialogMergeLicenceData {
  licenceA: OverviewLicenceDto;
  licenceB: OverviewLicenceDto;
}

export interface MergeOptionVm {
  name: string;
  icon: string;
  isBase: boolean;
}

// Rohform von GET /licences/{guid}/options: flaches Array von Option mit
// eingebettetem OptionType (Go-Default-JSON, daher Großschreibung).
interface BackendOption {
  OptionTypeID: number;
  OptionType?: {
    Name: string;
    Icon: string;
    LicenceOption: string;
  };
}

// Eine Lizenz "enthält die Basislizenz", wenn ihr Optionstyp-Code (LicenceOption)
// dem Muster BV?-0001* entspricht, z.B. BVE-0001, BVP-0001, BVE-0001-OA.
const BASE_LICENCE_OPTION_PATTERN = /^BV.-0001/;

@Component({
  selector: 'app-dialog-merge-licence',
  templateUrl: './dialog-merge-licence.component.html',
  styleUrls: ['./dialog-merge-licence.component.scss'],
})
export class DialogMergeLicenceComponent {
  primaryGuid: string;
  merging = false;

  optionsLoading = true;
  /** guid -> Optionen der Lizenz (Basis-Option zuerst) */
  optionsByGuid = new Map<string, MergeOptionVm[]>();

  constructor(
    public dialogRef: MatDialogRef<DialogMergeLicenceComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogMergeLicenceData,
    private restService: RestService,
  ) {
    this.primaryGuid = data.licenceA.guid;
    this.loadOptions();
  }

  get secondaryGuid(): string {
    return this.primaryGuid === this.data.licenceA.guid
      ? this.data.licenceB.guid
      : this.data.licenceA.guid;
  }

  get opexMismatch(): boolean {
    return this.data.licenceA.opex !== this.data.licenceB.opex;
  }

  get offlineMismatch(): boolean {
    return this.data.licenceA.offline !== this.data.licenceB.offline;
  }

  get mergeBlocked(): boolean {
    return this.opexMismatch || this.offlineMismatch;
  }

  get blockReason(): string {
    if (this.opexMismatch) {
      return 'OPEX- und CAPEX-Lizenzen können nicht zusammengeführt werden.';
    }
    if (this.offlineMismatch) {
      return 'Offline- und Online-Lizenzen können nicht zusammengeführt werden.';
    }
    return '';
  }

  labelFor(lic: OverviewLicenceDto): string {
    const name = lic.projectName || lic.vrs;
    return lic.easybillDocumentNumber ? `${name} (${lic.easybillDocumentNumber})` : name;
  }

  optionsFor(guid: string): MergeOptionVm[] {
    return this.optionsByGuid.get(guid) ?? [];
  }

  hasBase(guid: string): boolean {
    return this.optionsFor(guid).some(o => o.isBase);
  }

  private loadOptions(): void {
    this.optionsLoading = true;
    forkJoin({
      a: this.restService.get<BackendOption[]>(`/licences/${this.data.licenceA.guid}/options`),
      b: this.restService.get<BackendOption[]>(`/licences/${this.data.licenceB.guid}/options`),
    }).subscribe({
      next: ({ a, b }) => {
        this.optionsByGuid.set(this.data.licenceA.guid, this.toOptionVms(a));
        this.optionsByGuid.set(this.data.licenceB.guid, this.toOptionVms(b));
        this.optionsLoading = false;
      },
      error: (err) => {
        console.error('Loading licence options failed', err);
        this.optionsLoading = false;
      },
    });
  }

  private toOptionVms(options: BackendOption[]): MergeOptionVm[] {
    const vms: MergeOptionVm[] = [];
    for (const option of options ?? []) {
      const optionType = option.OptionType;
      if (!optionType) {
        continue;
      }
      vms.push({
        name: optionType.Name,
        icon: optionType.Icon || 'extension',
        isBase: BASE_LICENCE_OPTION_PATTERN.test(optionType.LicenceOption ?? ''),
      });
    }
    // Basis-Option(en) zuerst anzeigen
    return vms.sort((x, y) => Number(y.isBase) - Number(x.isBase));
  }

  merge(): void {
    if (this.mergeBlocked) {
      return;
    }
    this.merging = true;
    this.restService.post<any>(`/licences/${this.primaryGuid}/merge`, {
      secondary_guid: this.secondaryGuid,
    }).subscribe({
      next: () => this.dialogRef.close(true),
      error: (err) => {
        console.error('Merge failed', err);
        this.merging = false;
      },
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
